import { useEffect, useState, useCallback } from "react";
import {
  Container,
  Typography,
  Tabs,
  Tab,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Chip
} from "@mui/material";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";

import { Bar, Line } from "react-chartjs-2";
import API from "../api";
import * as XLSX from "xlsx";
import { format } from "date-fns";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export default function Reports() {
  const [tab, setTab] = useState(0);

  const [daily, setDaily] = useState(null);
  const [weekly, setWeekly] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [spotCheck, setSpotCheck] = useState([]);
  const [spotCheckGrandTotal, setSpotCheckGrandTotal] = useState(0);
  const [topProducts, setTopProducts] = useState([]);
  const [valuation, setValuation] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const dailyRes = await API.get("/reports/daily", {
        params: { t: Date.now() }
      });
      setDaily(dailyRes.data);
    } catch (err) {
      console.error("Daily Report Error:", err);
    }

    try {
      const weeklyRes = await API.get("/reports/weekly");
      setWeekly(Array.isArray(weeklyRes.data) ? weeklyRes.data : []);
    } catch (err) {
      console.error("Weekly Report Error:", err);
    }

    try {
      const monthlyRes = await API.get("/reports/monthly");
      setMonthly(Array.isArray(monthlyRes.data) ? monthlyRes.data : []);
    } catch (err) {
      console.error("Monthly Report Error:", err);
    }

    try {
      const spotRes = await API.get("/reports/spot_check");
      if (Array.isArray(spotRes.data)) {
        setSpotCheck(spotRes.data);
        setSpotCheckGrandTotal(
          spotRes.data.reduce((sum, sale) => sum + Number(sale.total || 0), 0)
        );
      } else {
        setSpotCheck(spotRes.data.sales || []);
        setSpotCheckGrandTotal(Number(spotRes.data.grand_total || 0));
      }
    } catch (err) {
      console.error("Spot Check Error:", err);
    }

    try {
      const topRes = await API.get("/reports/top-products");
      setTopProducts(Array.isArray(topRes.data) ? topRes.data : []);
    } catch (err) {
      console.error("Top Products Error:", err);
    }

    try {
      const valRes = await API.get("/reports/inventory-valuation");
      setValuation(valRes.data);
    } catch (err) {
      console.error("Valuation Report Error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const timer = setInterval(fetchAll, 30000);
    return () => clearInterval(timer);
  }, [fetchAll]);

  const fetchWithDates = async (type) => {
    if (!startDate || !endDate) {
      setError("Please select start and end dates.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const params = {
        start_date: format(startDate, "yyyy-MM-dd"),
        end_date: format(endDate, "yyyy-MM-dd")
      };

      const res = await API.get(`/reports/${type}`, { params });

      if (type === "weekly") {
        setWeekly(Array.isArray(res.data) ? res.data : []);
      }
      if (type === "monthly") {
        setMonthly(Array.isArray(res.data) ? res.data : []);
      }
    } catch (err) {
      console.error(err);
      setError(`Failed to fetch ${type} report.`);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = (data, fileName) => {
    if (!data) return;
    const exportData = Array.isArray(data) ? data : [data];
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, `${fileName}_${Date.now()}.xlsx`);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" fontWeight="bold">
            📊 Reports & Analytics
          </Typography>
          <Button variant="contained" onClick={fetchAll}>
            Refresh Reports
          </Button>
        </Box>

        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Daily" />
          <Tab label="Weekly" />
          <Tab label="Monthly" />
          <Tab label="Spot Check (60m)" />
          <Tab label="Top Products" />
          <Tab label="Valuation" />
        </Tabs>

        {loading && (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* DAILY REPORT */}
        {!loading && tab === 0 && (
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Today's Gross Sales
                    </Typography>
                    <Typography variant="h3" color="primary" fontWeight="bold">
                      {daily?.total_sales !== undefined
                        ? `$${Number(daily.total_sales).toFixed(2)}`
                        : "No Data"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Report Date: {daily?.date || "N/A"}
                    </Typography>
                    <Button
                      variant="contained"
                      sx={{ mt: 3 }}
                      disabled={!daily}
                      onClick={() => handleExport(daily, "daily_report")}
                    >
                      Export Daily Excel
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* WEEKLY REPORT */}
        {!loading && tab === 1 && (
          <Box>
            <Box sx={{ display: "flex", gap: 2, mb: 3, maxWidth: 600 }}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={setEndDate}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
              <Button variant="outlined" onClick={() => fetchWithDates("weekly")}>
                Apply
              </Button>
            </Box>

            <Paper sx={{ p: 3, mb: 2 }}>
              <Bar
                data={{
                  labels: weekly.map((r) => r.date),
                  datasets: [
                    {
                      label: "Sales ($)",
                      data: weekly.map((r) => r.total_sales),
                      backgroundColor: "rgba(25, 118, 210, 0.6)",
                      borderColor: "rgba(25, 118, 210, 1)",
                      borderWidth: 1
                    }
                  ]
                }}
              />
            </Paper>

            <Button variant="contained" onClick={() => handleExport(weekly, "weekly_report")}>
              Export Weekly Report
            </Button>
          </Box>
        )}

        {/* MONTHLY REPORT */}
        {!loading && tab === 2 && (
          <Box>
            <Box sx={{ display: "flex", gap: 2, mb: 3, maxWidth: 600 }}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={setEndDate}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
              <Button variant="outlined" onClick={() => fetchWithDates("monthly")}>
                Apply
              </Button>
            </Box>

            <Paper sx={{ p: 3, mb: 2 }}>
              <Line
                data={{
                  labels: monthly.map((r) => r.date),
                  datasets: [
                    {
                      label: "Sales ($)",
                      data: monthly.map((r) => r.total_sales),
                      borderColor: "#2e7d32",
                      backgroundColor: "rgba(46, 125, 50, 0.2)",
                      fill: true,
                      tension: 0.2
                    }
                  ]
                }}
              />
            </Paper>

            <Button variant="contained" onClick={() => handleExport(monthly, "monthly_report")}>
              Export Monthly Report
            </Button>
          </Box>
        )}

        {/* SPOT CHECK */}
        {!loading && tab === 3 && (
          <Box>
            <Paper elevation={2}>
              <Table>
                <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell><strong>Sale #</strong></TableCell>
                    <TableCell><strong>Cashier / User</strong></TableCell>
                    <TableCell><strong>Total ($)</strong></TableCell>
                    <TableCell><strong>Tendered ($)</strong></TableCell>
                    <TableCell><strong>Change ($)</strong></TableCell>
                    <TableCell><strong>Payment Method</strong></TableCell>
                    <TableCell><strong>Timestamp</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {spotCheck.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No transactions recorded in the last 60 minutes.
                      </TableCell>
                    </TableRow>
                  ) : (
                    spotCheck.map((s) => (
                      <TableRow key={s.sale_id}>
                        <TableCell>#{s.sale_id}</TableCell>
                        <TableCell>{s.cashier || "Unknown"}</TableCell>
                        <TableCell>${Number(s.total || 0).toFixed(2)}</TableCell>
                        <TableCell>${Number(s.tendered || 0).toFixed(2)}</TableCell>
                        <TableCell>${Number(s.change || 0).toFixed(2)}</TableCell>
                        <TableCell>
                          <Chip label={s.payment_method} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          {s.timestamp ? new Date(s.timestamp).toLocaleString() : ""}
                        </TableCell>
                      </TableRow>
                    ))
                  )}

                  <TableRow sx={{ backgroundColor: "#eeeeee" }}>
                    <TableCell colSpan={2}><strong>GRAND TOTAL</strong></TableCell>
                    <TableCell colSpan={5}>
                      <strong>${spotCheckGrandTotal.toFixed(2)}</strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>

            <Button
              variant="contained"
              sx={{ mt: 2 }}
              disabled={spotCheck.length === 0}
              onClick={() => handleExport(spotCheck, "spotcheck_report")}
            >
              Export Spot Check
            </Button>
          </Box>
        )}

        {/* TOP PRODUCTS */}
        {!loading && tab === 4 && (
          <Box>
            <Paper elevation={2}>
              <Table>
                <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell><strong>Product ID</strong></TableCell>
                    <TableCell><strong>Product Name</strong></TableCell>
                    <TableCell><strong>Units Sold</strong></TableCell>
                    <TableCell><strong>Revenue Generated ($)</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topProducts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No sales data found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    topProducts.map((p) => (
                      <TableRow key={p.product_id}>
                        <TableCell>#{p.product_id}</TableCell>
                        <TableCell><strong>{p.productname}</strong></TableCell>
                        <TableCell>{p.units_sold}</TableCell>
                        <TableCell>${Number(p.total_revenue || 0).toFixed(2)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Paper>

            <Button
              variant="contained"
              sx={{ mt: 2 }}
              disabled={topProducts.length === 0}
              onClick={() => handleExport(topProducts, "top_products_report")}
            >
              Export Top Products
            </Button>
          </Box>
        )}

        {/* VALUATION */}
        {!loading && tab === 5 && (
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Catalog Items
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {valuation?.total_catalog_items || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Total Inventory Units
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {valuation?.total_stock_units || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Total Inventory Asset Value
                    </Typography>
                    <Typography variant="h4" color="success.main" fontWeight="bold">
                      ${Number(valuation?.total_valuation || 0).toFixed(2)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Button
              variant="contained"
              sx={{ mt: 3 }}
              disabled={!valuation}
              onClick={() => handleExport(valuation, "inventory_valuation")}
            >
              Export Valuation Summary
            </Button>
          </Box>
        )}
      </Container>
    </LocalizationProvider>
  );
}